import { secp256k1 } from "@noble/curves/secp256k1.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { Any } from "cosmjs-types/google/protobuf/any.js";
import {
  AuthInfo,
  Fee,
  ModeInfo,
  SignDoc,
  SignerInfo,
  TxBody,
} from "cosmjs-types/cosmos/tx/v1beta1/tx.js";
import { SignMode } from "cosmjs-types/cosmos/tx/signing/v1beta1/signing.js";

import { encodeMsgExecuteCheckoutV2 } from "../probes/keplr-checkout-v2/codec.mjs";
import { validateAddress } from "../probes/keplr-send/core.mjs";
import { CHAIN, CHECKOUT_FEE, CHECKOUT_PROTOCOL } from "./config.mjs";
import { queryMerchantTerminal, queryProductOwner } from "./query.mjs";

const MESSAGE_KEYS = Object.freeze([
  "buyer", "merchant_address", "merchant_id", "terminal_id", "terminal_key_id",
  "chain_id", "checkout_id", "request_id", "request_hash", "created_at_ms",
  "expires_at_ms", "denomination", "total_amount", "products", "nonce",
  "ipi_card_commitment", "card_verification_config_id", "card_proof_identity",
  "card_read_counter", "terminal_signature",
]);
const ITEM_KEYS = Object.freeze([
  "product_id", "tag_uid", "quantity", "unit_price", "line_total", "expected_owner",
]);
const encoder = new TextEncoder();

export function bytesToHex(bytes) {
  return [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function hexToBytes(value, length, name) {
  if (typeof value !== "string" || !new RegExp(`^[0-9a-f]{${length * 2}}$`).test(value)) {
    throw new TypeError(`${name} must be ${length}-byte lowercase hex`);
  }
  return Uint8Array.from(value.match(/../g), (part) => Number.parseInt(part, 16));
}

function exactKeys(value, keys, name) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    throw new TypeError(`${name} schema is invalid`);
  }
}

function identifier(value, name) {
  if (typeof value !== "string" || !value || value.length > 128 || value.normalize("NFC") !== value) {
    throw new TypeError(`${name} is invalid`);
  }
  return value;
}

export function canonicalAmount(value, name = "amount", { positive = false } = {}) {
  if (typeof value !== "string" || !/^(?:0|[1-9][0-9]{0,77})$/.test(value)) {
    throw new TypeError(`${name} is not canonical aipi`);
  }
  if (positive && BigInt(value) === 0n) throw new TypeError(`${name} must be positive`);
  return value;
}

function canonicalItem(item) {
  exactKeys(item, ITEM_KEYS, "checkout item");
  const quantity = Number(item.quantity);
  if (!Number.isSafeInteger(quantity) || quantity <= 0 || quantity > 0xffffffff) {
    throw new TypeError("item quantity is invalid");
  }
  const unitPrice = canonicalAmount(item.unit_price, "unit_price", { positive: true });
  const lineTotal = canonicalAmount(item.line_total, "line_total", { positive: true });
  if (BigInt(unitPrice) * BigInt(quantity) !== BigInt(lineTotal)) {
    throw new TypeError("item line total is invalid");
  }
  return {
    product_id: identifier(item.product_id, "product_id"),
    tag_uid: identifier(item.tag_uid, "tag_uid"),
    quantity,
    unit_price: unitPrice,
    line_total: lineTotal,
    expected_owner: validateAddress(item.expected_owner),
  };
}

export function canonicalTerminalBody(message) {
  const products = message.products.map(canonicalItem)
    .sort((left, right) => left.product_id.localeCompare(right.product_id)
      || left.tag_uid.localeCompare(right.tag_uid));
  return {
    domain: CHECKOUT_PROTOCOL.domain,
    protocol_version: CHECKOUT_PROTOCOL.version,
    signature_algorithm: CHECKOUT_PROTOCOL.signatureAlgorithm,
    buyer: message.buyer,
    merchant_address: message.merchant_address,
    merchant_id: message.merchant_id,
    terminal_id: message.terminal_id,
    terminal_key_id: message.terminal_key_id,
    chain_id: message.chain_id,
    checkout_id: message.checkout_id,
    request_id: message.request_id,
    created_at_ms: message.created_at_ms,
    expires_at_ms: message.expires_at_ms,
    denomination: message.denomination,
    total_amount: message.total_amount,
    products,
    nonce: message.nonce,
    ipi_card_commitment: message.ipi_card_commitment,
    card_verification_config_id: message.card_verification_config_id,
    card_proof_identity: message.card_proof_identity,
    card_read_counter: message.card_read_counter,
  };
}

export function terminalRequestHash(message) {
  return bytesToHex(sha256(encoder.encode(JSON.stringify(canonicalTerminalBody(message)))));
}

export function canonicalTerminalSignBytes(message) {
  return encoder.encode(JSON.stringify({
    ...canonicalTerminalBody(message),
    request_hash: message.request_hash,
  }));
}

export function terminalKeyId(publicKeyHex) {
  return `terminal-key-${bytesToHex(sha256(hexToBytes(
    publicKeyHex,
    33,
    "terminal public key",
  ))).slice(0, 16)}`;
}

export function verifyTerminalSignature(message, publicKeyHex) {
  const signature = hexToBytes(message.terminal_signature, 64, "terminal signature");
  const publicKey = hexToBytes(publicKeyHex, 33, "terminal public key");
  return secp256k1.verify(signature, canonicalTerminalSignBytes(message), publicKey, {
    format: "compact",
    lowS: true,
    prehash: true,
  });
}

export function parseCheckoutRequest(text, {
  buyerAddress,
  nowMs = Date.now(),
} = {}) {
  let message;
  try { message = JSON.parse(String(text)); } catch { throw new TypeError("checkout request is not JSON"); }
  exactKeys(message, MESSAGE_KEYS, "checkout request");
  if (JSON.stringify(message) !== String(text).trim()) {
    throw new TypeError("checkout request is not canonically serialized");
  }
  validateAddress(message.buyer);
  validateAddress(message.merchant_address);
  if (buyerAddress && message.buyer !== buyerAddress) throw new TypeError("checkout buyer is not connected Keplr");
  for (const name of [
    "merchant_id", "terminal_id", "terminal_key_id", "checkout_id", "request_id",
    "card_verification_config_id",
  ]) identifier(message[name], name);
  if (message.chain_id !== CHAIN.chainId) throw new TypeError("checkout network is not IPI Testnet");
  if (message.denomination !== CHAIN.baseDenom) throw new TypeError("checkout denomination is invalid");
  canonicalAmount(message.total_amount, "total_amount", { positive: true });
  if (!Array.isArray(message.products) || message.products.length < 1 || message.products.length > 32) {
    throw new TypeError("checkout products are invalid");
  }
  const products = message.products.map(canonicalItem);
  const sum = products.reduce((total, item) => total + BigInt(item.line_total), 0n);
  if (sum !== BigInt(message.total_amount)) throw new TypeError("checkout total does not match items");
  if (!Number.isSafeInteger(message.created_at_ms) || !Number.isSafeInteger(message.expires_at_ms)) {
    throw new TypeError("checkout timestamps are invalid");
  }
  const lifetime = message.expires_at_ms - message.created_at_ms;
  if (
    lifetime < CHECKOUT_PROTOCOL.requestLifetimeMinMs
    || lifetime > CHECKOUT_PROTOCOL.requestLifetimeMaxMs
  ) throw new TypeError("checkout lifetime is invalid");
  if (message.created_at_ms > nowMs + CHECKOUT_PROTOCOL.allowedClockSkewMs) {
    throw new TypeError("checkout was created in the future");
  }
  if (nowMs > message.expires_at_ms + CHECKOUT_PROTOCOL.allowedClockSkewMs) {
    throw new TypeError("checkout has expired");
  }
  hexToBytes(message.nonce, 32, "nonce");
  hexToBytes(message.request_hash, 32, "request hash");
  hexToBytes(message.ipi_card_commitment, 32, "IPI Card commitment");
  hexToBytes(message.card_proof_identity, 32, "card proof identity");
  if (!Number.isSafeInteger(message.card_read_counter) || message.card_read_counter <= 0) {
    throw new TypeError("card read counter is invalid");
  }
  if (terminalRequestHash(message) !== message.request_hash) {
    throw new TypeError("checkout request hash is invalid");
  }
  return Object.freeze({ ...message, products: Object.freeze(products) });
}

export async function validateLiveCheckout(message, {
  fetchFn,
  balance,
  fee = CHECKOUT_FEE.amount,
  rpcRequest,
}) {
  const terminal = await queryMerchantTerminal(
    fetchFn,
    message.merchant_id,
    message.terminal_id,
    rpcRequest,
  );
  if (
    !terminal?.active
    || terminal.merchant_id !== message.merchant_id
    || terminal.terminal_id !== message.terminal_id
    || terminal.merchant_address !== message.merchant_address
    || terminal.terminal_key_id !== message.terminal_key_id
    || terminalKeyId(terminal.terminal_public_key) !== message.terminal_key_id
  ) throw new Error("terminal record does not authorize this checkout");
  if (!verifyTerminalSignature(message, terminal.terminal_public_key)) {
    throw new Error("terminal signature is invalid");
  }
  const products = [];
  for (const item of message.products) {
    const product = await queryProductOwner(fetchFn, item.product_id, rpcRequest);
    if (
      product?.product_id !== item.product_id
      || product.tag_uid !== item.tag_uid
      || product.current_owner !== item.expected_owner
      || product.current_owner !== message.merchant_address
      || !product.active
      || !product.transferable
    ) throw new Error(`product ${item.product_id} is not eligible for checkout`);
    products.push(product);
  }
  if (BigInt(message.total_amount) + BigInt(fee) > BigInt(balance)) {
    throw new Error("checkout total plus fee exceeds buyer balance");
  }
  return Object.freeze({ terminal, products });
}

function encodeEthPubkey(key) {
  if (!(key instanceof Uint8Array) || key.length !== 33) {
    throw new Error("expected a compressed ethsecp256k1 public key");
  }
  return Uint8Array.from([0x0a, key.length, ...key]);
}

export function makeCheckoutSignDoc({
  message,
  pubkey,
  accountNumber,
  sequence,
}) {
  const messageBytes = encodeMsgExecuteCheckoutV2(message).finish();
  const bodyBytes = TxBody.encode(TxBody.fromPartial({
    messages: [Any.fromPartial({ typeUrl: CHAIN.checkoutTypeUrl, value: messageBytes })],
    memo: "",
  })).finish();
  const authInfoBytes = AuthInfo.encode(AuthInfo.fromPartial({
    signerInfos: [SignerInfo.fromPartial({
      publicKey: Any.fromPartial({
        typeUrl: CHAIN.pubkeyTypeUrl,
        value: encodeEthPubkey(pubkey),
      }),
      modeInfo: ModeInfo.fromPartial({ single: { mode: SignMode.SIGN_MODE_DIRECT } }),
      sequence,
    })],
    fee: Fee.fromPartial({
      amount: [{ denom: CHAIN.baseDenom, amount: CHECKOUT_FEE.amount }],
      gasLimit: BigInt(CHECKOUT_FEE.gasLimit),
    }),
  })).finish();
  return SignDoc.fromPartial({
    bodyBytes,
    authInfoBytes,
    chainId: CHAIN.chainId,
    accountNumber,
  });
}

export function checkoutResultQr(receipt) {
  return JSON.stringify({
    protocol: "IPI_CHECKOUT_RESULT",
    version: 1,
    chain_id: CHAIN.chainId,
    tx_hash: receipt.txHash,
    height: receipt.height,
    code: receipt.code,
    checkout_id: receipt.checkoutId,
  });
}
